import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import { Article, CATEGORY_LABELS, RevisionHistoryEntry } from '../../types';
import { resolveImageUrl } from '../../utils/images';

// ─── Types ────────────────────────────────────────────────────────────────────

type FeedbackCategory =
  | 'factual_error'
  | 'wrong_tone'
  | 'headline_issue'
  | 'missing_info'
  | 'style_mismatch'
  | 'image_issue'
  | 'structure'
  | 'other';

type Severity = 'critical' | 'minor';

type FocusArea =
  | 'headline'
  | 'subheading'
  | 'lead_paragraph'
  | 'body'
  | 'image'
  | 'tags_category'
  | 'whole_article';

interface FeedbackEntry {
  id: string;
  articleId: string;
  articleHeadline: string;
  category: FeedbackCategory;
  severity: Severity;
  focusArea: FocusArea;
  freeText: string;
  submittedAt: string;
  revised: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const FEEDBACK_CATEGORIES: { key: FeedbackCategory; label: string; icon: string; desc: string }[] = [
  { key: 'factual_error',  label: 'Factual Error',    icon: 'error_outline',   desc: 'A number, date, name or claim is wrong' },
  { key: 'wrong_tone',     label: 'Wrong Tone',       icon: 'sentiment_dissatisfied', desc: 'Voice or register does not match AlCircle style' },
  { key: 'headline_issue', label: 'Headline Issue',   icon: 'title',           desc: 'Headline is inaccurate, weak or misleading' },
  { key: 'missing_info',   label: 'Missing Info',     icon: 'info',            desc: 'Important context or data is absent' },
  { key: 'style_mismatch', label: 'Style Mismatch',   icon: 'style',           desc: 'Formatting or terminology diverges from guidelines' },
  { key: 'image_issue',    label: 'Image Issue',      icon: 'broken_image',    desc: 'Generated image is wrong, irrelevant or low quality' },
  { key: 'structure',      label: 'Structure',        icon: 'reorder',         desc: 'Paragraphs or section order needs rearranging' },
  { key: 'other',          label: 'Other',            icon: 'more_horiz',      desc: 'Anything not covered above' },
];

const FOCUS_AREAS: { key: FocusArea; label: string }[] = [
  { key: 'whole_article',  label: 'Whole article' },
  { key: 'headline',       label: 'Headline' },
  { key: 'subheading',     label: 'Subheading' },
  { key: 'lead_paragraph', label: 'Lead paragraph' },
  { key: 'body',           label: 'Body' },
  { key: 'image',          label: 'Image' },
  { key: 'tags_category',  label: 'Tags / Category' },
];

const SEVERITY_CONFIG: Record<Severity, { label: string; color: string; bg: string; border: string; icon: string }> = {
  critical: { label: 'Critical — must fix before publish', color: '#ef4444', bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.3)', icon: 'warning' },
  minor:    { label: 'Minor — nice to fix',               color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.3)', icon: 'info' },
};

// ─── Helper ───────────────────────────────────────────────────────────────────

const buildFeedbackPrompt = (
  category: FeedbackCategory,
  severity: Severity,
  focusArea: FocusArea,
  freeText: string,
): string => {
  const catLabel = FEEDBACK_CATEGORIES.find(c => c.key === category)?.label || category;
  const areaLabel = FOCUS_AREAS.find(a => a.key === focusArea)?.label || focusArea;
  return `[${severity.toUpperCase()}] [${catLabel}] [${areaLabel}]\n\n${freeText}`;
};

// ─── Component ────────────────────────────────────────────────────────────────

const FeedbackDesk: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);

  // Form state
  const [category, setCategory]   = useState<FeedbackCategory>('factual_error');
  const [severity, setSeverity]   = useState<Severity>('critical');
  const [focusArea, setFocusArea] = useState<FocusArea>('whole_article');
  const [freeText, setFreeText]   = useState('');
  const [charCount, setCharCount] = useState(0);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<'success' | 'error' | null>(null);
  const [revisedContent, setRevisedContent] = useState<string | null>(null);
  const [revisionHistory, setRevisionHistory] = useState<RevisionHistoryEntry[]>([]);
  const [showDiff, setShowDiff] = useState(false);

  // Session feedback log
  const [feedbackLog, setFeedbackLog] = useState<FeedbackEntry[]>(() => {
    try {
      const stored = sessionStorage.getItem('feedback_session_log');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  const appendLog = (entry: FeedbackEntry) => {
    setFeedbackLog(prev => {
      const next = [entry, ...prev];
      try { sessionStorage.setItem('feedback_session_log', JSON.stringify(next)); } catch {}
      return next;
    });

    // Persist to backend (S3 + local disk)
    const user = (() => {
      try { return JSON.parse(localStorage.getItem('auth') || '{}')?.user?.email || ''; }
      catch { return ''; }
    })();
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    fetch(`${apiBase}/api/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...entry, submitted_by: user }),
    }).catch(() => { /* silent — sessionStorage backup already written */ });
  };

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Load articles
  useEffect(() => {
    apiService.getArticles()
      .then(data => { if (data?.articles) setArticles(data.articles); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Pre-select article from URL param (?articleId=...)
  useEffect(() => {
    const id = searchParams.get('articleId');
    if (id && articles.length > 0) {
      const found = articles.find(a => a.id === id);
      if (found) setSelectedArticle(found);
    }
  }, [searchParams, articles]);

  const filteredArticles = articles.filter(a => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return a.headline.toLowerCase().includes(q) || (a.news_source || '').toLowerCase().includes(q);
  });

  const handleSelectArticle = (article: Article) => {
    setSelectedArticle(article);
    setSubmitResult(null);
    setRevisedContent(null);
    setRevisionHistory([]);
    setShowDiff(false);
    setFreeText('');
    setCharCount(0);
    // Load history
    apiService.getArticleHistory(article.id, article.workflow_id || 'workflow_0')
      .then(data => { if (data?.revision_history) setRevisionHistory(data.revision_history); })
      .catch(() => {});
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFreeText(e.target.value);
    setCharCount(e.target.value.length);
  };

  const handleSubmit = async () => {
    if (!selectedArticle || !freeText.trim()) return;
    setIsSubmitting(true);
    setSubmitResult(null);

    const prompt = buildFeedbackPrompt(category, severity, focusArea, freeText);

    try {
      const response = await apiService.reviseArticle(
        selectedArticle.id,
        prompt,
        selectedArticle.workflow_id || 'workflow_0',
      );

      if (response.status === 'revised') {
        setRevisedContent(response.content);
        setSubmitResult('success');

        // Refresh history
        apiService.getArticleHistory(selectedArticle.id, selectedArticle.workflow_id || 'workflow_0')
          .then(data => { if (data?.revision_history) setRevisionHistory(data.revision_history); })
          .catch(() => {});

        // Update article in list
        setArticles(prev => prev.map(a =>
          a.id === selectedArticle.id ? { ...a, ...response, content: response.content } : a
        ));
        setSelectedArticle(prev => prev ? { ...prev, ...response, content: response.content } : prev);

        // Log it
        const entry: FeedbackEntry = {
          id: `fb_${Date.now()}`,
          articleId: selectedArticle.id,
          articleHeadline: selectedArticle.headline,
          category, severity, focusArea,
          freeText,
          submittedAt: new Date().toISOString(),
          revised: true,
        };
        appendLog(entry);
        setFreeText('');
        setCharCount(0);
      }
    } catch (err) {
      setSubmitResult('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const overallScore = selectedArticle
    ? Math.round((
        (selectedArticle.style_compliance_score || 0) +
        (selectedArticle.style_match_score || 0) +
        (selectedArticle.originality_score || (100 - (selectedArticle.plagiarism_score || 0)))
      ) / 3)
    : 0;

  return (
    <div className="flex flex-col w-full gap-6">

      {/* ── Page header ─────────────────────────────────────────────── */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-headline-lg tracking-tight" style={{ color: '#0f172a' }}>
            Feedback Desk
          </h1>
          <p className="font-body text-body-md mt-1" style={{ color: '#64748b' }}>
            Select an article, describe the problem, and the AI will revise it immediately.
          </p>
        </div>
        {feedbackLog.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg font-mono text-label-sm" style={{ background: 'rgba(79,70,229,0.1)', color: '#4f46e5' }}>
            <span className="material-symbols-outlined text-[14px]">history</span>
            {feedbackLog.length} submitted this session
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">

        {/* ── LEFT: Article picker ─────────────────────────────────── */}
        <div className="xl:col-span-4 flex flex-col gap-3">
          <div className="rounded-xl overflow-hidden" style={{ background: '#ffffff', border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>

            {/* Search */}
            <div className="p-3" style={{ borderBottom: '1px solid #f1f5f9' }}>
              <div className="relative flex items-center rounded-lg px-3 gap-2" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <span className="material-symbols-outlined text-[16px]" style={{ color: '#94a3b8' }}>search</span>
                <input
                  className="w-full bg-transparent py-2 font-body text-body-sm focus:outline-none"
                  style={{ color: '#0f172a' }}
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} style={{ color: '#94a3b8' }}>
                    <span className="material-symbols-outlined text-[14px]">close</span>
                  </button>
                )}
              </div>
            </div>

            {/* Article list */}
            <div className="overflow-y-auto" style={{ maxHeight: 520 }}>
              {loading ? (
                <div className="flex items-center justify-center py-10">
                  <span className="font-mono text-label-sm animate-pulse" style={{ color: '#94a3b8' }}>Loading articles...</span>
                </div>
              ) : filteredArticles.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center px-4">
                  <span className="material-symbols-outlined text-[32px] mb-2" style={{ color: '#cbd5e1' }}>article</span>
                  <p className="font-body text-body-sm" style={{ color: '#94a3b8' }}>No articles found</p>
                </div>
              ) : (
                filteredArticles.map(article => {
                  const isSelected = selectedArticle?.id === article.id;
                  const imageUrl = resolveImageUrl(article.generated_image_url || article.imageUrl);
                  const score = Math.round(((article.style_compliance_score || 0) + (article.style_match_score || 0) + (article.originality_score || (100 - (article.plagiarism_score || 0)))) / 3);
                  const scoreColor = score >= 85 ? '#34d399' : score >= 70 ? '#4f46e5' : '#ef4444';

                  return (
                    <div
                      key={article.id}
                      onClick={() => handleSelectArticle(article)}
                      className="flex items-start gap-3 p-3 cursor-pointer transition-all"
                      style={{
                        borderBottom: '1px solid #f8fafc',
                        background: isSelected ? 'rgba(79,70,229,0.06)' : 'transparent',
                        borderLeft: isSelected ? '3px solid #4f46e5' : '3px solid transparent',
                      }}
                    >
                      {/* Thumbnail */}
                      <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0" style={{ background: '#0f172a' }}>
                        {imageUrl
                          ? <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                          : <div className="w-full h-full flex items-center justify-center"><span className="material-symbols-outlined text-[20px]" style={{ color: 'rgba(255,255,255,0.2)' }}>article</span></div>
                        }
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-display text-body-md leading-snug line-clamp-2 mb-1" style={{ color: isSelected ? '#4f46e5' : '#0f172a' }}>
                          {article.headline}
                        </h3>
                        <div className="flex items-center gap-2">
                          <StatusChip status={article.status} />
                          <span className="font-mono text-[10px]" style={{ color: '#94a3b8' }}>
                            Q: <span style={{ color: scoreColor, fontWeight: 700 }}>{score}%</span>
                          </span>
                          {(article.revision_count || 0) > 0 && (
                            <span className="font-mono text-[10px]" style={{ color: '#94a3b8' }}>
                              · rev ×{article.revision_count}
                            </span>
                          )}
                        </div>
                      </div>

                      {isSelected && (
                        <span className="material-symbols-outlined text-[18px] flex-shrink-0 mt-1" style={{ color: '#4f46e5' }}>chevron_right</span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Session log */}
          {feedbackLog.length > 0 && (
            <div className="rounded-xl overflow-hidden" style={{ background: '#ffffff', border: '1px solid rgba(0,0,0,0.07)' }}>
              <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid #f1f5f9' }}>
                <span className="material-symbols-outlined text-[16px]" style={{ color: '#4f46e5' }}>history</span>
                <span className="font-display text-headline-sm" style={{ color: '#0f172a' }}>Session Log</span>
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: 240 }}>
                {feedbackLog.map(entry => (
                  <div key={entry.id} className="px-4 py-3" style={{ borderBottom: '1px solid #f8fafc' }}>
                    <div className="flex items-start gap-2 mb-1">
                      <span
                        className="px-1.5 py-0.5 rounded font-mono text-[9px] font-bold uppercase flex-shrink-0"
                        style={{ background: entry.severity === 'critical' ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)', color: entry.severity === 'critical' ? '#ef4444' : '#f59e0b' }}
                      >
                        {entry.severity}
                      </span>
                      <span className="font-body text-[11px] line-clamp-1" style={{ color: '#64748b' }}>
                        {FEEDBACK_CATEGORIES.find(c => c.key === entry.category)?.label}
                      </span>
                    </div>
                    <p className="font-body text-[11px] line-clamp-2 mb-1" style={{ color: '#0f172a' }}>{entry.freeText}</p>
                    <p className="font-mono text-[10px]" style={{ color: '#94a3b8' }}>
                      {entry.articleHeadline.slice(0, 40)}… · {new Date(entry.submittedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── RIGHT: Feedback form + result ────────────────────────── */}
        <div className="xl:col-span-8 flex flex-col gap-4">

          {!selectedArticle ? (
            /* Empty prompt */
            <div className="rounded-xl flex flex-col items-center justify-center py-20 text-center" style={{ background: '#ffffff', border: '2px dashed #e2e8f0' }}>
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(79,70,229,0.08)' }}>
                <span className="material-symbols-outlined text-[28px]" style={{ color: '#4f46e5' }}>rate_review</span>
              </div>
              <h2 className="font-display text-headline-sm mb-2" style={{ color: '#0f172a' }}>Select an article</h2>
              <p className="font-body text-body-sm max-w-xs" style={{ color: '#64748b' }}>
                Pick an article from the list on the left to start giving feedback.
              </p>
            </div>
          ) : (
            <>
              {/* Article preview card */}
              <div className="rounded-xl overflow-hidden" style={{ background: '#ffffff', border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                {/* Header bar */}
                <div className="px-5 py-4 flex items-start justify-between gap-4" style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {selectedArticle.category && (
                        <span className="font-mono text-label-xs px-2 py-0.5 rounded uppercase" style={{ background: 'rgba(79,70,229,0.1)', color: '#4f46e5' }}>
                          {CATEGORY_LABELS[selectedArticle.category] || selectedArticle.category}
                        </span>
                      )}
                      <StatusChip status={selectedArticle.status} />
                      {(selectedArticle.revision_count || 0) > 0 && (
                        <span className="font-mono text-label-xs" style={{ color: '#94a3b8' }}>Revised ×{selectedArticle.revision_count}</span>
                      )}
                    </div>
                    <h2 className="font-display text-headline-md leading-snug" style={{ color: '#0f172a' }}>
                      {selectedArticle.headline}
                    </h2>
                    {selectedArticle.subheading && (
                      <p className="font-body text-body-sm mt-1 line-clamp-2" style={{ color: '#64748b' }}>{selectedArticle.subheading}</p>
                    )}
                  </div>
                  <button
                    onClick={() => navigate(`/article/${selectedArticle.id}`)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg font-body text-body-sm flex-shrink-0 transition-all"
                    style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569' }}
                  >
                    <span className="material-symbols-outlined text-[14px]">open_in_new</span>
                    Full editor
                  </button>
                </div>

                {/* Score strip */}
                <div className="grid grid-cols-4 divide-x" style={{ borderBottom: '1px solid #f1f5f9' }}>
                  {[
                    { label: 'Overall',     value: overallScore,                                                                              color: overallScore >= 85 ? '#34d399' : overallScore >= 70 ? '#4f46e5' : '#ef4444' },
                    { label: 'Style',       value: selectedArticle.style_match_score || 0,                                                    color: (selectedArticle.style_match_score || 0) >= 80 ? '#34d399' : '#4f46e5' },
                    { label: 'Originality', value: selectedArticle.originality_score || (100 - (selectedArticle.plagiarism_score || 0)),       color: (selectedArticle.originality_score || (100 - (selectedArticle.plagiarism_score || 0))) >= 80 ? '#34d399' : '#4f46e5' },
                    { label: 'Confidence',  value: selectedArticle.style_compliance_score || selectedArticle.confidence_score || 0,            color: (selectedArticle.style_compliance_score || 0) >= 80 ? '#34d399' : '#4f46e5' },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="px-4 py-3 text-center">
                      <div className="font-display text-headline-md" style={{ color }}>{Math.round(value)}%</div>
                      <div className="font-mono text-label-xs" style={{ color: '#94a3b8' }}>{label}</div>
                    </div>
                  ))}
                </div>

                {/* Article excerpt */}
                <div className="px-5 py-4">
                  <p className="font-body text-body-md line-clamp-3" style={{ color: '#475569', lineHeight: 1.7 }}>
                    {selectedArticle.content.slice(0, 320).replace(/\n/g, ' ')}…
                  </p>
                  <a href={selectedArticle.news_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 font-body text-body-sm" style={{ color: '#94a3b8' }}>
                    <span className="material-symbols-outlined text-[13px]">link</span>
                    {selectedArticle.news_source}
                  </a>
                </div>
              </div>

              {/* Feedback form */}
              <div className="rounded-xl" style={{ background: '#ffffff', border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <div className="px-5 py-4" style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <h3 className="font-display text-headline-sm" style={{ color: '#0f172a' }}>Submit Feedback</h3>
                  <p className="font-body text-body-sm mt-0.5" style={{ color: '#64748b' }}>The AI will revise the article based on your input.</p>
                </div>

                <div className="p-5 flex flex-col gap-5">

                  {/* Category grid */}
                  <div>
                    <label className="font-mono text-label-xs uppercase tracking-wider block mb-2" style={{ color: '#94a3b8' }}>
                      What type of problem?
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {FEEDBACK_CATEGORIES.map(cat => {
                        const active = category === cat.key;
                        return (
                          <button
                            key={cat.key}
                            onClick={() => setCategory(cat.key)}
                            className="flex flex-col items-start gap-1.5 p-3 rounded-xl text-left transition-all"
                            style={{
                              background: active ? 'rgba(79,70,229,0.08)' : '#f8fafc',
                              border: active ? '1.5px solid #4f46e5' : '1.5px solid #e2e8f0',
                            }}
                            title={cat.desc}
                          >
                            <span className="material-symbols-outlined text-[18px]" style={{ color: active ? '#4f46e5' : '#94a3b8' }}>{cat.icon}</span>
                            <span className="font-display text-body-md" style={{ color: active ? '#4f46e5' : '#475569' }}>{cat.label}</span>
                          </button>
                        );
                      })}
                    </div>
                    {/* Category description */}
                    <p className="mt-2 font-body text-body-sm" style={{ color: '#94a3b8' }}>
                      {FEEDBACK_CATEGORIES.find(c => c.key === category)?.desc}
                    </p>
                  </div>

                  {/* Severity + Focus area row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Severity */}
                    <div>
                      <label className="font-mono text-label-xs uppercase tracking-wider block mb-2" style={{ color: '#94a3b8' }}>
                        Severity
                      </label>
                      <div className="flex flex-col gap-2">
                        {(['critical', 'minor'] as Severity[]).map(sev => {
                          const cfg = SEVERITY_CONFIG[sev];
                          const active = severity === sev;
                          return (
                            <button
                              key={sev}
                              onClick={() => setSeverity(sev)}
                              className="flex items-center gap-3 p-3 rounded-xl text-left transition-all"
                              style={{
                                background: active ? cfg.bg : '#f8fafc',
                                border: `1.5px solid ${active ? cfg.border : '#e2e8f0'}`,
                              }}
                            >
                              <span className="material-symbols-outlined text-[18px]" style={{ color: active ? cfg.color : '#94a3b8' }}>{cfg.icon}</span>
                              <span className="font-body text-body-sm" style={{ color: active ? cfg.color : '#475569' }}>{cfg.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Focus area */}
                    <div>
                      <label className="font-mono text-label-xs uppercase tracking-wider block mb-2" style={{ color: '#94a3b8' }}>
                        Which part?
                      </label>
                      <div className="flex flex-col gap-1.5">
                        {FOCUS_AREAS.map(area => {
                          const active = focusArea === area.key;
                          return (
                            <button
                              key={area.key}
                              onClick={() => setFocusArea(area.key)}
                              className="flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-all"
                              style={{
                                background: active ? 'rgba(79,70,229,0.08)' : '#f8fafc',
                                border: `1px solid ${active ? '#4f46e5' : '#e2e8f0'}`,
                                color: active ? '#4f46e5' : '#475569',
                              }}
                            >
                              {active && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#4f46e5' }} />}
                              <span className="font-body text-body-sm">{area.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Free-text */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="font-mono text-label-xs uppercase tracking-wider" style={{ color: '#94a3b8' }}>
                        Describe the problem
                      </label>
                      <span className="font-mono text-label-xs" style={{ color: charCount > 500 ? '#ef4444' : '#94a3b8' }}>
                        {charCount} / 600
                      </span>
                    </div>
                    <textarea
                      ref={textareaRef}
                      value={freeText}
                      onChange={handleTextChange}
                      onKeyDown={e => {
                        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && freeText.trim().length >= 10 && !isSubmitting) {
                          e.preventDefault();
                          handleSubmit();
                        }
                      }}
                      maxLength={600}
                      rows={5}
                      placeholder={
                        category === 'factual_error'  ? 'e.g. "The LME aluminium price is stated as $2,400/t but the source says $2,280/t."' :
                        category === 'wrong_tone'     ? 'e.g. "Lead paragraph reads as a press release rather than neutral reporting."' :
                        category === 'headline_issue' ? 'e.g. "Headline implies demand fell globally but the data only covers Europe."' :
                        category === 'missing_info'   ? 'e.g. "No mention of Rusal\'s production capacity, which the source article covers."' :
                        category === 'image_issue'    ? 'e.g. "Image shows a steel plant, not an aluminium smelter."' :
                        'Describe what is wrong and how you would like it fixed...'
                      }
                      className="w-full rounded-xl p-4 font-body text-body-md resize-none focus:outline-none transition-all"
                      style={{
                        background: '#f8fafc',
                        border: '1.5px solid #e2e8f0',
                        color: '#0f172a',
                        lineHeight: 1.6,
                      }}
                      onFocus={e => { e.currentTarget.style.borderColor = '#4f46e5'; e.currentTarget.style.background = '#fff'; }}
                      onBlur={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = '#f8fafc'; }}
                    />
                    <div className="flex items-center justify-between mt-1.5">
                      {freeText.length > 0 && freeText.length < 30 ? (
                        <p className="font-body text-body-sm" style={{ color: '#f59e0b' }}>
                          More detail helps the AI make a precise fix.
                        </p>
                      ) : <span />}
                      <span className="font-mono text-[10px]" style={{ color: '#cbd5e1' }}>
                        Ctrl+Enter to submit
                      </span>
                    </div>
                  </div>

                  {/* Submit */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting || !freeText.trim() || freeText.length < 10}
                      className="flex items-center gap-2 px-6 py-3 rounded-xl font-display text-headline-sm transition-all disabled:opacity-50"
                      style={{
                        background: isSubmitting ? '#f1f5f9' : 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                        color: isSubmitting ? '#94a3b8' : '#ffffff',
                        boxShadow: isSubmitting ? 'none' : '0 4px 16px rgba(79,70,229,0.3)',
                      }}
                    >
                      <span className={`material-symbols-outlined text-[18px] ${isSubmitting ? 'animate-spin' : ''}`}>
                        {isSubmitting ? 'progress_activity' : 'send'}
                      </span>
                      {isSubmitting ? 'Revising article...' : 'Submit & Revise'}
                    </button>

                    {submitResult === 'error' && (
                      <div className="flex items-center gap-2 font-body text-body-sm" style={{ color: '#ef4444' }}>
                        <span className="material-symbols-outlined text-[16px]">error</span>
                        Revision failed — please try again.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Revision result ──────────────────────────────────── */}
              {submitResult === 'success' && revisedContent && (
                <div className="rounded-xl overflow-hidden" style={{ background: '#ffffff', border: '1px solid rgba(52,211,153,0.3)', boxShadow: '0 0 0 3px rgba(52,211,153,0.08)' }}>
                  <div className="px-5 py-4 flex items-center justify-between" style={{ background: 'rgba(52,211,153,0.06)', borderBottom: '1px solid rgba(52,211,153,0.2)' }}>
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-[18px]" style={{ color: '#34d399' }}>check_circle</span>
                      <span className="font-display text-headline-sm" style={{ color: '#065f46' }}>Article revised successfully</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowDiff(!showDiff)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-body text-body-sm transition-all"
                        style={{ background: '#f0fdf4', border: '1px solid rgba(52,211,153,0.3)', color: '#065f46' }}
                      >
                        <span className="material-symbols-outlined text-[14px]">difference</span>
                        {showDiff ? 'Hide diff' : 'Show diff'}
                      </button>
                      <button
                        onClick={() => navigate(`/article/${selectedArticle.id}`)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-body text-body-sm transition-all"
                        style={{ background: '#4f46e5', color: '#ffffff' }}
                      >
                        <span className="material-symbols-outlined text-[14px]">open_in_new</span>
                        Open article
                      </button>
                    </div>
                  </div>

                  {showDiff && (
                    <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x" style={{}}>
                      <div className="p-4">
                        <div className="flex items-center gap-1.5 mb-3">
                          <span className="w-2 h-2 rounded-full" style={{ background: '#ef4444' }} />
                          <span className="font-mono text-label-xs uppercase" style={{ color: '#94a3b8' }}>Before</span>
                        </div>
                        <p className="font-body text-body-sm leading-relaxed line-clamp-6" style={{ color: '#64748b' }}>
                          {selectedArticle.content.slice(0, 600)}
                        </p>
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-1.5 mb-3">
                          <span className="w-2 h-2 rounded-full" style={{ background: '#34d399' }} />
                          <span className="font-mono text-label-xs uppercase" style={{ color: '#94a3b8' }}>After</span>
                        </div>
                        <p className="font-body text-body-sm leading-relaxed line-clamp-6" style={{ color: '#0f172a' }}>
                          {revisedContent.slice(0, 600)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Revision history ─────────────────────────────────── */}
              {revisionHistory.length > 0 && (
                <div className="rounded-xl overflow-hidden" style={{ background: '#ffffff', border: '1px solid rgba(0,0,0,0.07)' }}>
                  <div className="px-5 py-4 flex items-center gap-2" style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <span className="material-symbols-outlined text-[18px]" style={{ color: '#4f46e5' }}>manage_history</span>
                    <span className="font-display text-headline-sm" style={{ color: '#0f172a' }}>Revision History</span>
                    <span className="font-mono text-label-xs ml-1" style={{ color: '#94a3b8' }}>{revisionHistory.length} revision{revisionHistory.length > 1 ? 's' : ''}</span>
                  </div>
                  <div className="divide-y" style={{}}>
                    {revisionHistory.map((entry, i) => (
                      <RevisionRow key={i} entry={entry} index={revisionHistory.length - i} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const cfg: Record<string, { color: string; bg: string; label: string }> = {
    draft:        { color: '#0ea5e9', bg: 'rgba(14,165,233,0.1)',  label: 'Draft' },
    published:    { color: '#34d399', bg: 'rgba(52,211,153,0.1)',  label: 'Published' },
    revised:      { color: '#4f46e5', bg: 'rgba(79,70,229,0.1)',  label: 'Revised' },
    needs_review: { color: '#ef4444', bg: 'rgba(239,68,68,0.1)',   label: 'Needs Review' },
    review:       { color: '#64748b', bg: 'rgba(100,116,139,0.1)', label: 'In Review' },
  };
  const c = cfg[status] || { color: '#94a3b8', bg: '#f1f5f9', label: status };
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-mono text-[10px] font-semibold" style={{ color: c.color, background: c.bg }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.color }} />
      {c.label}
    </span>
  );
};

const RevisionRow: React.FC<{ entry: RevisionHistoryEntry; index: number }> = ({ entry, index }) => {
  const [open, setOpen] = useState(false);
  const ts = entry.timestamp ? new Date(entry.timestamp).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-5 py-3 text-left transition-all"
        style={{ background: open ? '#fafafa' : 'transparent' }}
      >
        <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 font-mono text-[10px] font-bold" style={{ background: 'rgba(79,70,229,0.12)', color: '#4f46e5' }}>
          {index}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-body text-body-sm line-clamp-1" style={{ color: '#0f172a' }}>
            {entry.feedback || 'No feedback text recorded'}
          </p>
        </div>
        <span className="font-mono text-[10px] flex-shrink-0" style={{ color: '#94a3b8' }}>{ts}</span>
        <span className={`material-symbols-outlined text-[16px] transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} style={{ color: '#cbd5e1' }}>expand_more</span>
      </button>

      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x mx-5 mb-3 rounded-xl overflow-hidden" style={{ border: '1px solid #e2e8f0' }}>
          <div className="p-4">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="w-2 h-2 rounded-full" style={{ background: '#ef4444' }} />
              <span className="font-mono text-label-xs uppercase" style={{ color: '#94a3b8' }}>Before</span>
            </div>
            <p className="font-body text-body-sm leading-relaxed" style={{ color: '#64748b', maxHeight: 160, overflow: 'auto' }}>
              {entry.previous_content?.slice(0, 400) || 'Not recorded'}
            </p>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="w-2 h-2 rounded-full" style={{ background: '#34d399' }} />
              <span className="font-mono text-label-xs uppercase" style={{ color: '#94a3b8' }}>After</span>
            </div>
            <p className="font-body text-body-sm leading-relaxed" style={{ color: '#0f172a', maxHeight: 160, overflow: 'auto' }}>
              {entry.revised_content?.slice(0, 400) || 'Not recorded'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackDesk;
